import {
  IconAlertTriangle,
  IconPlayerPlay,
  IconRefresh,
  IconSend,
} from '@tabler/icons-react'
import type { UseMutationResult } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useParams } from 'react-router'

import { useSession } from '../auth/useSession'
import { CanWrite } from '../components/CanWrite'
import { CopyButton } from '../components/CopyButton'
import { Dialog } from '../components/Dialog'
import { JsonDisclosure } from '../components/JsonDisclosure'
import {
  Alert,
  Badge,
  EmptyState,
  ErrorState,
  FieldErrorText,
  LoadingState,
} from '../components/States'
import { ApiError, fieldMessage } from '../lib/apiClient'
import type {
  DeliveryView,
  PaymentDetail,
  ResolveReviewInput,
  SimulateOutcome,
} from '../lib/apiTypes'
import { formatDateTimeWib, formatDuration, formatRupiah } from '../lib/format'
import {
  usePayment,
  useRedeliver,
  useRefreshPayment,
  useResolveReview,
  useSimulatePayment,
} from '../lib/hooks'
import {
  deliveryStatusLabel,
  deliveryStatusTone,
  eventTypeLabel,
  paymentMethodLabel,
  paymentStatusLabel,
  paymentStatusTone,
} from '../lib/status'

/**
 * Detail transaksi: ringkasan, timeline event, komunikasi bank yang bisa
 * dibuka per baris, riwayat webhook dengan kirim ulang, dan aksi operator
 * (cek status ke bank, simulasi sandbox, selesaikan tinjauan).
 */
export function TransactionDetailPage() {
  const { id = '' } = useParams()
  const detail = usePayment(id)
  const { canWrite } = useSession()
  const [dialog, setDialog] = useState<'none' | 'resolve'>('none')
  const [feedback, setFeedback] = useState<{ tone: 'success' | 'danger'; text: string } | null>(
    null,
  )

  const refresh = useRefreshPayment(id)
  const simulate = useSimulatePayment(id)
  const redeliver = useRedeliver(id)
  const resolve = useResolveReview(id)

  if (detail.isPending) {
    return <LoadingState what="detail transaksi" />
  }

  if (detail.isError) {
    const notFound = detail.error instanceof ApiError && detail.error.status === 404
    return (
      <>
        <BackLink />
        <ErrorState
          title={notFound ? 'Transaksi tidak ditemukan' : 'Gagal memuat detail transaksi'}
          error={detail.error}
          onRetry={notFound ? undefined : () => void detail.refetch()}
          retrying={detail.isFetching}
          hint={
            notFound
              ? 'Periksa kembali tautan atau buka daftar transaksi untuk mencari dengan filter.'
              : 'Server admin tidak membalas data yang diminta. Coba lagi sebentar lagi.'
          }
        />
      </>
    )
  }

  const payment = detail.data.payment
  const isSandbox = payment.provider === 'sandbox' && payment.environment === 'sandbox' && !(payment.needs_review && ['amount_mismatch', 'amount_unreadable'].includes(payment.review_reason))

  return (
    <>
      <BackLink />

      <div className="page-head">
        <div className="page-head__text">
          <h1>Detail transaksi</h1>
          <p className="mono">{payment.id}</p>
        </div>
        <div className="page-head__actions">
          <CanWrite>
            <button
              type="button"
              className="btn"
              onClick={() => {
                setFeedback(null)
                refresh.mutate(undefined, {
                  onSuccess: () => setFeedback({ tone: 'success', text: 'Status sudah dicek ke bank.' }),
                  onError: (error) => setFeedback({ tone: 'danger', text: actionError(error) }),
                })
              }}
              disabled={refresh.isPending}
            >
              <IconRefresh size={18} stroke={1.75} aria-hidden="true" />
              {refresh.isPending ? 'Mengecek...' : 'Cek status ke bank'}
            </button>
          </CanWrite>
        </div>
      </div>

      {feedback ? <Alert tone={feedback.tone}>{feedback.text}</Alert> : null}

      {payment.needs_review ? (
        <Alert tone="warning">
          Transaksi ini ditandai perlu tinjauan: {payment.review_reason || 'tanpa alasan tertulis'}.
          {canWrite
            ? ' Selesaikan tinjauan setelah mencocokkan nominal dengan mutasi rekening.'
            : ' Hanya admin yang bisa menyelesaikan tinjauan.'}
        </Alert>
      ) : null}

      <div className="grid-2 grid-2--wide">
        <section className="card" aria-labelledby="summary-heading">
          <div className="card__head">
            <h2 className="card__title" id="summary-heading">
              Ringkasan
            </h2>
            <Badge tone={paymentStatusTone(payment.status)}>
              {paymentStatusLabel(payment.status)}
            </Badge>
          </div>

          <div className="split">
            <Fact label="Nominal diminta" value={formatRupiah(payment.amount_minor)} lead />
            <Fact
              label="Nominal dibayar"
              value={
                payment.paid_amount_minor === null
                  ? 'belum dibayar'
                  : formatRupiah(payment.paid_amount_minor)
              }
            />
            <Fact label="Merchant" value={`${payment.merchant} (${payment.environment})`} />
            <Fact label="Kanal" value={paymentMethodLabel(payment.method)} />
            {payment.method === 'va' ? (
              <Fact label="Nomor VA" value={payment.va_number || 'belum ada'} mono />
            ) : null}
            <Fact label="Client" value={payment.client} />
            <Fact label="Referensi client" value={payment.client_reference || 'tidak diisi'} mono />
            <Fact
              label="Referensi bank"
              value={payment.provider_reference_no || payment.partner_reference_no || 'belum ada'}
              mono
            />
            <Fact label="RRN" value={payment.rrn || 'belum ada'} mono />
            <Fact label="Percobaan" value={`ke-${payment.attempt_no}`} />
            <Fact label="Dibuat" value={formatDateTimeWib(payment.created_at)} />
            <Fact label="Kedaluwarsa" value={formatDateTimeWib(payment.expires_at)} />
            <Fact
              label="Dibayar"
              value={payment.paid_at ? formatDateTimeWib(payment.paid_at) : 'belum dibayar'}
            />
            <Fact label="Terakhir diperbarui" value={formatDateTimeWib(payment.updated_at)} />
            {payment.method === 'qris' && detail.data.qr_content ? (
              <div className="fact form-grid--full">
                <span className="fact__label">Isi QR</span>
                <div className="copy-row">
                  <code className="copy-row__value">{detail.data.qr_content}</code>
                  <CopyButton value={detail.data.qr_content} label="Salin isi QR" small />
                </div>
              </div>
            ) : null}
          </div>

          {payment.paid_after_final ? (
            <p className="card__hint">
              Bank mengonfirmasi pembayaran setelah transaksi melewati batas waktu. Status
              tetap dicatat lunas dan webhook pembayaran dikirim.
            </p>
          ) : null}

          {payment.failure_reason ? (
            <p className="card__hint">Alasan gagal: {payment.failure_reason}</p>
          ) : null}
        </section>

        <section className="card" aria-labelledby="actions-heading">
          <div className="card__head">
            <h2 className="card__title" id="actions-heading">
              Tindakan operator
            </h2>
          </div>

          {canWrite ? (
            <div className="rows">
              <div className="row-item">
                <div className="row-item__main">
                  <span className="row-item__title">Selesaikan tinjauan</span>
                  <span className="row-item__meta">
                    Wajib disertai alasan. Nominal wajib bila memilih tandai lunas.
                  </span>
                </div>
                <button
                  type="button"
                  className="btn btn--sm"
                  disabled={!payment.needs_review}
                  onClick={() => setDialog('resolve')}
                >
                  Selesaikan
                </button>
              </div>

              {isSandbox ? (
                <div className="row-item">
                  <div className="row-item__main">
                    <span className="row-item__title">Simulasi pembayaran sandbox</span>
                    <span className="row-item__meta">
                      Hanya untuk provider sandbox lokal, bukan sandbox bank. Hasilnya langsung mengubah status
                      transaksi dan menjadwalkan webhook.
                    </span>
                  </div>
                  <div className="state__actions">
                    {(['paid', 'failed', 'expired'] as SimulateOutcome[]).map((outcome) => (
                      <button
                        key={outcome}
                        type="button"
                        className="btn btn--sm"
                        disabled={simulate.isPending}
                        onClick={() => {
                          setFeedback(null)
                          simulate.mutate(outcome, {
                            onSuccess: () =>
                              setFeedback({
                                tone: 'success',
                                text: `Simulasi "${outcome}" diterapkan.`,
                              }),
                            onError: (error) =>
                              setFeedback({ tone: 'danger', text: actionError(error) }),
                          })
                        }}
                      >
                        <IconPlayerPlay size={16} stroke={1.75} aria-hidden="true" />
                        {outcome}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="row-item">
                  <div className="row-item__main">
                    <span className="row-item__title">Simulasi pembayaran</span>
                    <span className="row-item__meta">
                      Tidak tersedia: provider {payment.provider} berjalan di lingkungan {payment.environment}. Simulasi hanya untuk provider sandbox lokal di lingkungan sandbox tanpa tinjauan nominal aktif.
                    </span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="muted">
              Peran viewer hanya bisa membaca. Aksi yang mengubah data disembunyikan dan
              ditolak server dengan kode 403.
            </p>
          )}
        </section>
      </div>

      <section className="card" aria-labelledby="events-heading">
        <div className="card__head">
          <h2 className="card__title" id="events-heading">
            Timeline event
          </h2>
          <span className="card__hint">{detail.data.events.length} event tercatat</span>
        </div>
        {detail.data.events.length === 0 ? (
          <EmptyState
            title="Belum ada event"
            body="Event ditulis setiap kali status berubah atau operator menjalankan aksi. Transaksi baru biasanya sudah punya event pembuatan."
          />
        ) : (
          <ol className="timeline">
            {detail.data.events.map((event) => (
              <li className="timeline__item" key={event.id}>
                <span className="timeline__time">{formatDateTimeWib(event.created_at)}</span>
                <div className="timeline__body">
                  <span className="timeline__title">{eventTypeLabel(event.type)}</span>
                  <p className="timeline__meta">
                    {event.from_status || event.to_status
                      ? `${event.from_status || 'awal'} menuju ${event.to_status || 'tanpa status'}`
                      : 'tanpa perubahan status'}
                    {event.actor ? `, oleh ${event.actor}` : ''}
                    {`, sumber ${event.source}`}
                  </p>
                  <JsonDisclosure summary="Buka data event" value={event.data} />
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>

      <section className="card" aria-labelledby="exchanges-heading">
        <div className="card__head">
          <h2 className="card__title" id="exchanges-heading">
            Komunikasi bank
          </h2>
          <span className="card__hint">
            {detail.data.exchanges.length} permintaan tercatat, isi rahasia sudah disamarkan
          </span>
        </div>
        {detail.data.exchanges.length === 0 ? (
          <EmptyState
            title="Belum ada komunikasi dengan bank"
            body="Permintaan ke bank dicatat di sini setelah pembuatan QRIS atau nomor VA, cek status, atau notifikasi masuk."
          />
        ) : (
          <div className="rows">
            {detail.data.exchanges.map((exchange) => (
              <article className="row-item" key={exchange.id}>
                <div className="row-item__main">
                  <span className="row-item__title">
                    {exchange.direction} {exchange.operation}
                  </span>
                  <span className="row-item__meta mono">{exchange.method} {exchange.url}</span>
                  <span className="row-item__meta">
                    {formatDateTimeWib(exchange.created_at)}, kredensial {exchange.credential},
                    {` status ${exchange.response_status ?? 'tanpa balasan'}`}
                    {`, ${formatDuration(exchange.duration_ms)}`}
                    {exchange.verification ? `, verifikasi ${exchange.verification}` : ''}
                  </span>
                  {exchange.error ? (
                    <span className="row-item__meta">Error: {exchange.error}</span>
                  ) : null}
                </div>
                <div>
                  <JsonDisclosure summary="Header permintaan" value={exchange.request_headers} />
                  <JsonDisclosure summary="Isi permintaan" value={exchange.request_body} />
                  <JsonDisclosure summary="Isi balasan" value={exchange.response_body} />
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="card" aria-labelledby="deliveries-heading">
        <div className="card__head">
          <h2 className="card__title" id="deliveries-heading">
            Riwayat webhook
          </h2>
          <span className="card__hint">{detail.data.deliveries.length} delivery</span>
        </div>
        {detail.data.deliveries.length === 0 ? (
          <EmptyState
            title="Belum ada webhook untuk transaksi ini"
            body="Webhook dijadwalkan saat status berubah. Transaksi yang masih menunggu pembayaran belum punya delivery."
          />
        ) : (
          <div className="rows">
            {detail.data.deliveries.map((delivery) => (
              <DeliveryRow
                key={delivery.id}
                delivery={delivery}
                onRedeliver={() => {
                  setFeedback(null)
                  redeliver.mutate(delivery.id, {
                    onSuccess: () => setFeedback({ tone: 'success', text: 'Webhook dijadwalkan kirim ulang.' }),
                    onError: (error) => setFeedback({ tone: 'danger', text: actionError(error) }),
                  })
                }}
                busy={redeliver.isPending}
              />
            ))}
          </div>
        )}
      </section>

      {dialog === 'resolve' ? (
        <ResolveDialog
          payment={detail.data}
          resolve={resolve}
          onClose={() => { resolve.reset(); setDialog('none') }}
          onDone={(message) => {
            setDialog('none')
            setFeedback({ tone: 'success', text: message })
          }}
        />
      ) : null}
    </>
  )
}

function BackLink() {
  return (
    <p>
      <Link to="/transactions">Kembali ke daftar transaksi</Link>
    </p>
  )
}

function Fact({
  label,
  value,
  mono,
  lead,
}: {
  label: string
  value: string
  mono?: boolean
  lead?: boolean
}) {
  const className = `fact__value num${lead ? ' fact__value--lead' : ''}${mono ? ' mono' : ''}`
  return (
    <div className="fact">
      <span className="fact__label">{label}</span>
      <span className={className}>{value}</span>
    </div>
  )
}

function DeliveryRow({
  delivery,
  onRedeliver,
  busy,
}: {
  delivery: DeliveryView
  onRedeliver: () => void
  busy: boolean
}) {
  const { canWrite } = useSession()
  return (
    <article className="row-item">
      <div className="row-item__main">
        <span className="row-item__title">{delivery.event_type}</span>
        <span className="row-item__meta">
          Percobaan {delivery.attempts}, berikutnya {formatDateTimeWib(delivery.next_attempt_at)}
        </span>
        <span className="row-item__meta">
          {delivery.last_status_code
            ? `Balasan terakhir HTTP ${delivery.last_status_code}`
            : 'Belum ada balasan HTTP'}
          {delivery.last_error ? `, ${delivery.last_error}` : ''}
        </span>
        {delivery.history.length > 0 ? (
          <JsonDisclosure
            summary={`Buka ${delivery.history.length} percobaan`}
            value={delivery.history.map((attempt) => ({
              percobaan: attempt.attempt_no,
              kode_http: attempt.status_code,
              error: attempt.error,
              durasi_ms: attempt.duration_ms,
              waktu: attempt.created_at,
            }))}
          />
        ) : null}
      </div>
      <div className="state__actions">
        <Badge tone={deliveryStatusTone(delivery.status)}>
          {deliveryStatusLabel(delivery.status)}
        </Badge>
        {canWrite ? (
          <button type="button" className="btn btn--sm" onClick={onRedeliver} disabled={busy}>
            <IconSend size={16} stroke={1.75} aria-hidden="true" />
            Kirim ulang
          </button>
        ) : null}
      </div>
    </article>
  )
}

/**
 * Dialog penyelesaian tinjauan. Alasan wajib diisi, dan nominal wajib ketika
 * memilih tandai lunas; keduanya diperiksa di klien dengan pesan yang sama
 * seperti yang akan dikirim server.
 */
function ResolveDialog({
  payment,
  resolve,
  onClose,
  onDone,
}: {
  payment: PaymentDetail
  resolve: UseMutationResult<PaymentDetail, Error, ResolveReviewInput>
  onClose: () => void
  onDone: (message: string) => void
}) {
  const [resolution, setResolution] = useState<'dismiss' | 'mark_paid'>('dismiss')
  const [note, setNote] = useState('')
  const [amount, setAmount] = useState('')
  const [localError, setLocalError] = useState('')

  const amountMinor = parseAmountToMinor(amount)
  const noteMissing = note.trim().length === 0
  const amountMissing = resolution === 'mark_paid' && amountMinor === null

  function submit() {
    if (noteMissing) {
      setLocalError('Alasan wajib diisi, 1 sampai 500 karakter.')
      return
    }
    if (amountMissing) {
      setLocalError('Nominal wajib diisi bila memilih tandai lunas.')
      return
    }
    setLocalError('')
    resolve.mutate(
      {
        resolution,
        note: note.trim(),
        paid_amount_minor: resolution === 'mark_paid' ? (amountMinor ?? undefined) : undefined,
      },
      {
        onSuccess: () =>
          onDone(
            resolution === 'mark_paid'
              ? 'Tinjauan diselesaikan: transaksi ditandai lunas.'
              : 'Tinjauan diselesaikan: transaksi diabaikan.',
          ),
        onError: () => setLocalError(''),
      },
    )
  }

  const serverError = resolve.error

  return (
    <Dialog
      title="Selesaikan tinjauan"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Batal
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={submit}
            disabled={resolve.isPending}
          >
            {resolve.isPending ? 'Menyimpan...' : 'Simpan keputusan'}
          </button>
        </>
      }
    >
      <p className="muted">
        Nominal diminta {formatRupiah(payment.payment.amount_minor)}. Alasan keputusan
        tercatat di audit log bersama identitas operator.
      </p>

      <div className="radio-group" role="radiogroup" aria-label="Keputusan tinjauan">
        <label>
          <input
            type="radio"
            name="resolution"
            value="dismiss"
            checked={resolution === 'dismiss'}
            onChange={() => setResolution('dismiss')}
          />
          Abaikan: status transaksi tidak diubah
        </label>
        <label>
          <input
            type="radio"
            name="resolution"
            value="mark_paid"
            checked={resolution === 'mark_paid'}
            onChange={() => setResolution('mark_paid')}
          />
          Tandai lunas dengan nominal tertentu
        </label>
      </div>

      {resolution === 'mark_paid' ? (
        <div className="field">
          <label className="field__label" htmlFor="resolve-amount">
            Nominal dibayar (rupiah)
          </label>
          <input
            id="resolve-amount"
            className="input num"
            inputMode="numeric"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            aria-describedby="resolve-amount-hint"
            aria-invalid={Boolean(fieldMessage(serverError, 'paid_amount_minor')) || undefined}
          />
          <span className="field__hint" id="resolve-amount-hint">
            Isi bilangan bulat rupiah tanpa titik, misalnya 125000. Nominal ini dikirim ke
            server dalam satuan sen.
          </span>
          <FieldErrorText error={serverError} field="paid_amount_minor" />
        </div>
      ) : null}

      <div className="field">
        <label className="field__label" htmlFor="resolve-note">
          Alasan
        </label>
        <textarea
          id="resolve-note"
          className="textarea"
          value={note}
          maxLength={500}
          onChange={(event) => setNote(event.target.value)}
          aria-describedby="resolve-note-hint"
          aria-invalid={Boolean(fieldMessage(serverError, 'note')) || undefined}
        />
        <span className="field__hint" id="resolve-note-hint">
          Tuliskan dasar keputusan, misalnya nomor mutasi rekening atau hasil penelusuran.
          Wajib diisi, maksimal 500 karakter.
        </span>
        <FieldErrorText error={serverError} field="note" />
      </div>

      {localError ? (
        <Alert tone="danger">
          <IconAlertTriangle size={18} stroke={1.75} aria-hidden="true" /> {localError}
        </Alert>
      ) : null}

      {serverError ? <Alert tone="danger">{actionError(serverError)}</Alert> : null}
    </Dialog>
  )
}

/** Bilangan bulat rupiah menjadi sen; null bila isian tidak sah. */
function parseAmountToMinor(value: string): number | null {
  const digits = value.replace(/[.\s]/g, '')
  if (!/^\d+$/.test(digits)) {
    return null
  }
  const rupiah = Number(digits)
  if (!Number.isFinite(rupiah) || rupiah <= 0) {
    return null
  }
  return rupiah * 100
}

function actionError(error: unknown): string {
  if (error instanceof ApiError) {
    const detail = error.fields.map((field) => field.message).join(' ')
    return detail
      ? `${error.message} ${detail}`
      : `${error.message} (kode HTTP ${error.status}${error.code ? `, ${error.code}` : ''})`
  }
  return 'Aksi gagal karena penyebab yang tidak diketahui.'
}
